// src/Components/Dashboard/StudentDashboard/PlacementTestStatus.js
import { Box, Button, Chip, LinearProgress, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { CONTACT_EMAIL } from "config/contact";

const fallbackRules = [
  { minScore: 90, maxScore: 100, resultStatus: "PASS", allowBooking: true },
  {
    minScore: 75,
    maxScore: 89,
    resultStatus: "PASS_WITH_RECOMMENDATION",
    allowBooking: true,
  },
  {
    minScore: 60,
    maxScore: 74,
    resultStatus: "CONDITIONAL_REVIEW",
    allowBooking: false,
  },
  { minScore: 0, maxScore: 59, resultStatus: "FAIL", allowBooking: false },
];

function pickMessage(item, lang) {
  if (!item) return "";
  if (lang?.startsWith("ar")) {
    return item.studentMessageAr || "";
  }
  if (lang?.startsWith("de")) {
    return item.studentMessageDe || "";
  }
  return item.studentMessageEn || item.studentMessage || item.studentMessageDe || "";
}

function getActiveRule(rules, score, resultStatus) {
  if (!Array.isArray(rules) || rules.length === 0) return null;
  return (
    rules.find(
      (r) =>
        Number(score) >= Number(r.minScore) &&
        Number(score) <= Number(r.maxScore)
    ) ||
    rules.find((r) => r.resultStatus === resultStatus) ||
    null
  );
}

export default function PlacementTestStatus() {
  const { t, i18n } = useTranslation();
  const { status, statusLoading, statusError } = useSelector(
    (s) => s.studentPlacement
  );

  if (statusLoading) return null;
  if (statusError) return null;
  if (!status?.hasSession) return null;

  const {
    inProgress,
    submitted,
    blacklisted,
    awaitingTeacherReview,
    finalized,
    finalLevel,
    targetLevel,
    normalizedScore,
    allowBooking,
    resultStatus,
    evaluationRules,
  } = status || {};

  const supportEmail = CONTACT_EMAIL;
  const orderedRules = [...(evaluationRules?.length ? evaluationRules : fallbackRules)]
    .sort((a, b) => Number(b.minScore) - Number(a.minScore));
  const activeRule = getActiveRule(orderedRules, normalizedScore, resultStatus);
  const activeMessage = pickMessage(status, i18n.language) || pickMessage(activeRule, i18n.language);
  const displayScore = Number.isFinite(Number(normalizedScore))
    ? Number(normalizedScore)
    : 0;
  const recommendedLevel = finalLevel || activeRule?.finalLevel || targetLevel || "A1";
  const recommendedCoursesUrl = `/german-language?level=${encodeURIComponent(recommendedLevel)}#courses-section`;
  const getStatusTitle = (statusKey) =>
    t(`placementStatus.resultCard.statusTitle.${statusKey}`, {
      defaultValue:
        statusKey ||
        t("placementStatus.resultCard.placementResult", {
          defaultValue: "Placement result",
        }),
    });

  return (
    <>
      <Box
        sx={{
          border: "1px solid #e5e7eb",
          borderRadius: 3,
          p: { xs: 2, sm: 3 },
          bgcolor: "#fff",
          width: { xs: "100%", sm: "80%" },
          boxShadow: "0 14px 35px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 800 }}>
            {finalized
              ? t("placementStatus.resultCard.title", { defaultValue: "Your placement result" })
              : t("placementStatus.title")}
          </Typography>
          {targetLevel && (
            <Chip
              size="small"
              label={`${t("placementStatus.resultCard.targetLabel", { defaultValue: "Target" })}: ${targetLevel}`}
            />
          )}
        </Stack>

        {!finalized ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {awaitingTeacherReview
              ? t("placementStatus.summary.awaitingReview")
              : submitted
              ? t("placementStatus.summary.submitted")
              : inProgress
              ? t("placementStatus.summary.inProgress")
              : t("placementStatus.summary.notStarted")}
          </Typography>
        ) : (
          <Stack spacing={2}>
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                background:
                  "linear-gradient(135deg, rgba(255,193,7,0.18), rgba(255,255,255,1))",
                border: "1px solid rgba(245, 158, 11, 0.35)",
              }}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
                <Box
                  sx={{
                    width: 88,
                    height: 88,
                    borderRadius: "50%",
                    bgcolor: "#ffc107",
                    color: "#111827",
                    display: "grid",
                    placeItems: "center",
                    fontWeight: 900,
                    fontSize: 26,
                    boxShadow: "0 10px 24px rgba(245, 158, 11, 0.35)",
                    flex: "0 0 auto",
                  }}
                >
                  {displayScore}
                </Box>
                <Box flex={1}>
                  <Typography variant="overline" color="text.secondary">
                    {activeRule?.minScore ?? 0}–{activeRule?.maxScore ?? 100}{" "}
                    {t("placementStatus.resultCard.pointsLabel", { defaultValue: "points" })}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
                    {getStatusTitle(activeRule?.resultStatus)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#374151" }}>
                    {activeMessage ||
                      t("placementStatus.resultCard.finalRecommendedLevel", {
                        level: finalLevel ?? "-",
                        defaultValue: `Your final recommended level is ${finalLevel ?? "-"}.`,
                      })}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    href={recommendedCoursesUrl}
                    sx={{ mt: 1.5, bgcolor: "#ffc107", color: "#111827", fontWeight: 800, "&:hover": { bgcolor: "#f5b700" } }}
                  >
                    {t("placementStatus.resultCard.viewRecommendedCourses", {
                      level: recommendedLevel,
                      defaultValue: `View recommended ${recommendedLevel} courses`,
                    })}
                  </Button>
                </Box>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.max(0, Math.min(100, displayScore))}
                sx={{
                  mt: 2,
                  height: 9,
                  borderRadius: 99,
                  bgcolor: "rgba(17, 24, 39, 0.08)",
                  "& .MuiLinearProgress-bar": { bgcolor: "#ffc107" },
                }}
              />
            </Box>

            <Stack spacing={1}>
              {orderedRules.map((rule) => {
                const isActive = activeRule === rule;
                return (
                  <Box
                    key={`${rule.minScore}-${rule.maxScore}`}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: isActive ? "2px solid #ffc107" : "1px solid #e5e7eb",
                      bgcolor: isActive ? "#fff8db" : "#fafafa",
                    }}
                  >
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }}>
                      <Chip
                        size="small"
                        label={`${rule.minScore}–${rule.maxScore} ${t("placementStatus.resultCard.pointsLabel", { defaultValue: "points" })}`}
                        color={isActive ? "warning" : "default"}
                        sx={{ fontWeight: 800 }}
                      />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                          {getStatusTitle(rule.resultStatus)}
                          {isActive
                            ? ` · ${t("placementStatus.resultCard.yourRange", { defaultValue: "Your range" })}`
                            : ""}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {pickMessage(rule, i18n.language)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Stack>
        )}

        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
          {blacklisted && <Chip size="small" label={t("placementStatus.badges.blacklisted")} color="error" />}
          {inProgress && !submitted && <Chip size="small" label={t("placementStatus.badges.inProgress")} />}
          {submitted && <Chip size="small" label={t("placementStatus.badges.submitted")} />}
          {awaitingTeacherReview && <Chip size="small" label={t("placementStatus.badges.awaitingReview")} />}
          {finalized && <Chip size="small" label={t("placementStatus.badges.finalized")} color="success" />}
          {finalized && finalLevel && (
            <Chip
              size="small"
              label={`${t("placementStatus.resultCard.finalLevelLabel", { defaultValue: "Final level" })}: ${finalLevel}`}
              variant="outlined"
            />
          )}
          {finalized && allowBooking !== null && allowBooking !== undefined && (
            <Chip
              size="small"
              label={
                allowBooking
                  ? t("placementStatus.resultCard.bookingAllowed", { defaultValue: "Booking allowed" })
                  : t("placementStatus.resultCard.reviewRecommendedFirst", { defaultValue: "Review recommended first" })
              }
              color={allowBooking ? "success" : "warning"}
              variant="outlined"
            />
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          mt: 5,
          width: { xs: "100%", sm: "80%" },
          bgcolor: "#fbf9e0ff",
          padding: 1.5,
          borderRadius: 2,
          border: "1px solid #f0c000",
        }}
      >
        <Typography variant="body2">
          {t("placementStatus.supportPrefix")}
          <a
            href={`mailto:${supportEmail}`}
            style={{
              fontWeight: 700,
              color: "#1976d2",
              textDecoration: "none",
              marginInline: 4,
            }}
          >
            {supportEmail}
          </a>
          {t("placementStatus.supportSuffix")}
        </Typography>
      </Box>
    </>
  );
}
